import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import useEventListener from './useEventListener';
import { IUseLocalStorage } from '@monofront/types';

type TStorageCustomEventData = { hookId: number };

declare global {
  interface WindowEventMap {
    'local-storage': CustomEvent<TStorageCustomEventData>;
  }
}

type SetValue<T> = Dispatch<SetStateAction<T>>;

let hookCounter = 0;

/**
 * @param key
 * @param initialValue
 * @returns an array with the value stored parsed and his setter
 */
export const useSynchronousStorage = (getStorage: () => Storage) =>
  (<T>(key: string, initialValue: T) => {
    const [hookId] = useState(() => Date.now() + hookCounter++);

    // Get from local storage then
    // parse stored json or return initialValue
    const readValue = useCallback((): T => {
      // Prevent build error "window is undefined" but keeps working
      if (typeof window === 'undefined') {
        return initialValue;
      }

      try {
        const item = getStorage().getItem(key);

        return item ? (parseJSON(item) as T) : initialValue;
      } catch (error) {
        return initialValue;
      }
    }, [initialValue, key]);

    // State to store our value
    // Use initialValue for SSR consistency; useEffect below syncs from storage after hydration
    const [storedValue, setStoredValue] = useState<T>(initialValue);

    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to storage.
    const setValue: SetValue<T> = useCallback((value) => {
      try {
        // Allow value to be a function so we have the same API as useState
        const newValue = value instanceof Function ? value(storedValue) : value;

        getStorage().setItem(key, JSON.stringify(newValue));

        setStoredValue(newValue);

        // We dispatch a custom event so every useSynchronousStorage hook are notified
        window.dispatchEvent(
          new CustomEvent<TStorageCustomEventData>('local-storage', {
            detail: { hookId },
          }),
        );
      } catch (error) {
        console.warn(`Error setting storage key “${key}”:`, error);
      }
    }, []);

    useEffect(() => {
      setStoredValue(readValue());
    }, []);

    const handleStorageChange = useCallback(
      (event: StorageEvent | CustomEvent<TStorageCustomEventData>) => {
        if ('key' in event) {
          // Event comes from another tab/window
          if (event.key === key) {
            setStoredValue(readValue());
          }

          return;
        } else if (event.detail?.hookId === hookId) {
          // Event comes from the same hook, skip parsing again
          return;
        }

        setStoredValue(readValue());
      },
      [key, readValue],
    );

    // this only works for other pages, not the current one
    useEventListener('storage', handleStorageChange);

    // custom event, triggered in setValue
    useEventListener('local-storage', handleStorageChange);

    return [
      storedValue,
      setValue,
      {
        deleteItem: () => {
          getStorage().removeItem(key);
          setStoredValue(initialValue); // keep local state in sync
          window.dispatchEvent(
            new CustomEvent<TStorageCustomEventData>('local-storage', {
              detail: { hookId },
            }),
          );
        },
      },
    ];
  }) satisfies IUseLocalStorage;

// A wrapper for "JSON.parse()"" to support "undefined" value
function parseJSON<T>(value: string | null): T | undefined {
  try {
    return value === 'undefined' ? undefined : JSON.parse(value ?? '');
  } catch {
    return undefined;
  }
}
